import React, { useContext, useEffect, useState } from "react";
import { Box, Button, Container, LinearProgress, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import currency from "currency.js";
import bkstApi from "../../api";
import BDatePicker from "../../common/input/BDatePicker";
import { getQueryParam, getStoreFilterStreets, getStoreLocationIdForFilter } from "../../common/util";
import ItemFilter from "../../planner/component/ItemFilter";
import { UserContext } from "../../context/UserContext";

const ACCENT = "#78374a";
const MUTED = "#818181";
const PANEL_BG = "#FFFFFF";
const NESTED_BG = "#F3F1F2";
const BAR_TRACK = "#E8E4E5";

function money(value) {
  return currency(value || 0).format();
}

function pct(part, whole) {
  if (!whole) return 0;
  return Math.round(((part || 0) / whole) * 100);
}

function orderLabel(count) {
  return `${count || 0} ${(count || 0) === 1 ? "order" : "orders"}`;
}

function Section({ title, description, children }) {
  return (
    <Box
      sx={{
        bgcolor: PANEL_BG,
        borderRadius: 1,
        p: { xs: 2.5, sm: 3.5 },
        mb: 2.5,
      }}
    >
      {title ? (
        <Typography variant="h4" sx={{ mb: description ? 1 : 2.5 }}>
          {title}
        </Typography>
      ) : null}
      {description ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function Subsection({ title, children }) {
  return (
    <Box sx={{ mb: 3, "&:last-child": { mb: 0 } }}>
      {title ? (
        <Typography variant="h5" sx={{ mb: 2 }}>
          {title}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function ShareLegend({ segments, total }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mt: 1.5 }}>
      {segments.map((segment) => (
        <Typography key={segment.key} variant="caption" color="text.secondary">
          <Box
            component="span"
            sx={{
              display: "inline-block",
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: segment.color,
              mr: 0.75,
            }}
          />
          {`${segment.label} ${pct(segment.value, total)}%`}
        </Typography>
      ))}
    </Box>
  );
}

function ShareBar({ segments, total }) {
  return (
    <Box sx={{ mb: 3 }}>
      {!total ? (
        <Box sx={{ height: 10, borderRadius: 5, bgcolor: BAR_TRACK }} />
      ) : (
        <Box
          sx={{
            display: "flex",
            height: 10,
            borderRadius: 5,
            overflow: "hidden",
            bgcolor: BAR_TRACK,
          }}
        >
          {segments.map((segment) => {
            const width = pct(segment.value, total);
            if (!width) return null;
            return (
              <Box
                key={segment.key}
                title={`${segment.label}: ${money(segment.value)} (${width}%)`}
                sx={{
                  width: `${width}%`,
                  bgcolor: segment.color,
                  minWidth: 4,
                }}
              />
            );
          })}
        </Box>
      )}
      <ShareLegend segments={segments} total={total} />
    </Box>
  );
}

function BreakdownRow({ label, amount, count, countNoun = "order", nested, detail }) {
  const noun = count === 1 ? countNoun : `${countNoun}s`;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: nested ? 1.25 : 1.75,
        pl: nested ? 2 : 0,
        borderBottom: "1px solid #EEE",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" title={label} sx={{ wordBreak: "break-word" }}>
          {label}
        </Typography>
        {detail ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {detail}
          </Typography>
        ) : null}
      </Box>
      <Box sx={{ textAlign: "right", flexShrink: 0 }}>
        <Typography variant="body2" fontWeight={600}>
          {money(amount)}
        </Typography>
        {count != null ? (
          <Typography variant="caption" color="text.secondary">
            {`${count || 0} ${noun}`}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function NestedList({ title, children }) {
  if (!children) return null;
  return (
    <Box sx={{ mt: 2.5 }}>
      {title ? (
        <Typography
          variant="overline"
          color="text.secondary"
          display="block"
          sx={{ mb: 0.75, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
      ) : null}
      <Box sx={{ bgcolor: PANEL_BG, borderRadius: 1, px: 2, py: 0.5 }}>{children}</Box>
    </Box>
  );
}

/** Grey category card — amount, label, meta, optional nested white list */
function CategoryCard({
  label,
  amount,
  count,
  share,
  countNoun = "order",
  detail,
  nestedTitle,
  children,
}) {
  const noun = count === 1 ? countNoun : `${countNoun}s`;
  return (
    <Box sx={{ bgcolor: NESTED_BG, borderRadius: 1, p: { xs: 2.5, sm: 3 }, minWidth: 0 }}>
      <Typography variant="h3" sx={{ lineHeight: 1.2, wordBreak: "break-word" }}>
        {money(amount)}
      </Typography>
      <Typography
        variant="overline"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.75, lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        {`${count || 0} ${noun}${typeof share === "number" ? ` · ${share}%` : ""}`}
      </Typography>
      {detail ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          {detail}
        </Typography>
      ) : null}
      <NestedList title={nestedTitle}>{children}</NestedList>
    </Box>
  );
}

function CategoryGrid({ columns = 1, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: columns === 1 ? "1fr" : `repeat(${Math.min(columns, 2)}, 1fr)`,
          md: `repeat(${columns}, 1fr)`,
        },
        gap: 2.5,
      }}
    >
      {children}
    </Box>
  );
}

function getPosPaymentMethods(pos) {
  return [
    {
      key: "cash",
      label: "Cash",
      totalAmount: pos.cash?.totalAmount,
      orderCount: pos.cash?.orderCount,
    },
    {
      key: "card",
      label: "Card",
      totalAmount: pos.card?.totalAmount,
      orderCount: pos.card?.orderCount,
    },
    {
      key: "other",
      label: "Other",
      totalAmount: pos.other?.totalAmount,
      orderCount: pos.other?.orderCount,
    },
  ].filter((m) => (m.totalAmount || 0) > 0 || (m.orderCount || 0) > 0);
}

export default function EndOfDayReport() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [date, setDate] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [storeFilter, setStoreFilter] = useState({});
  const storeStreets = getStoreFilterStreets(user);
  const showStoreFilter = storeStreets.length > 1;

  const yesterday = moment().subtract(1, "day").format("YYYY-MM-DD");
  const maxReportDate = moment(yesterday).toDate();

  useEffect(() => {
    let qDate = getQueryParam(location.search, "date");
    if (!qDate || qDate > yesterday) {
      qDate = yesterday;
    }
    search(qDate, getStoreLocationIdForFilter(user, storeFilter?.store?.value));
  }, [location.search]);

  const runDateSearch = (e) => {
    e.preventDefault();
    if (!date || date > yesterday) return;
    navigate(`/report/end-of-day?date=${date}`);
  };

  const onDateChange = (d) => {
    if (!d || d > yesterday) return;
    setDate(d);
  };

  const search = (reportDate, storeLocationId = "") => {
    setLoading(true);
    setError("");
    const locQ = storeLocationId ? `&storeLocationId=${encodeURIComponent(storeLocationId)}` : "";
    bkstApi(`/my/report/end-of-day?date=${reportDate}${locQ}`)
      .then((res) => {
        setReport(res.data.report);
        setDate(reportDate);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.response?.data?.error || e.message || "Failed to load end of day report");
        setLoading(false);
      });
  };

  const onStoreFilterChange = (o) => {
    const next = { ...storeFilter, ...o };
    const prevId = getStoreLocationIdForFilter(user, storeFilter?.store?.value);
    const nextId = getStoreLocationIdForFilter(user, next?.store?.value);
    setStoreFilter(next);
    if (date && prevId !== nextId) {
      search(date, nextId);
    }
  };

  const summary = report?.summary || {};
  const fulfillment = report?.fulfillment || {};
  const transactions = report?.transactions || {};
  const channel = report?.channel || {};
  const customers = report?.customers || {};
  const totalSales = summary.totalSales || 0;
  const paymentsTotal = transactions.total?.amount || 0;
  const customersTotal = (customers.new?.totalAmount || 0) + (customers.repeat?.totalAmount || 0);

  const summarySegments = [
    { key: "itemSales", label: "Item Sales", value: summary.itemSales || 0, color: ACCENT },
    { key: "taxes", label: "Taxes", value: summary.taxes || 0, color: MUTED },
    { key: "tips", label: "Tips", value: summary.tips || 0, color: "#C4A4AD" },
  ];
  const summaryPartsTotal = summarySegments.reduce((sum, s) => sum + (s.value || 0), 0);

  const fulfillmentSegments = [
    { key: "togo", label: "To-Go", value: fulfillment.togo?.totalAmount || 0, color: ACCENT },
    { key: "future", label: "Future", value: fulfillment.future?.totalAmount || 0, color: MUTED },
  ];

  const paymentSegments = [
    { key: "card", label: "Card", value: transactions.card?.amount || 0, color: ACCENT },
    { key: "cash", label: "Cash", value: transactions.cash?.amount || 0, color: "#A86B7C" },
    { key: "other", label: "Other", value: transactions.other?.amount || 0, color: MUTED },
  ];

  const channelSegments = [
    { key: "online", label: "Online", value: channel.online?.totalAmount || 0, color: ACCENT },
    {
      key: "inStore",
      label: "In-Store",
      value: channel.inStore?.totalAmount || 0,
      color: "#A86B7C",
    },
    {
      key: "commercial",
      label: "Commercial",
      value: channel.commercial?.totalAmount || 0,
      color: MUTED,
    },
  ];

  const customerSegments = [
    { key: "new", label: "New", value: customers.new?.totalAmount || 0, color: ACCENT },
    { key: "repeat", label: "Repeat", value: customers.repeat?.totalAmount || 0, color: MUTED },
  ];

  const onlineUtms = channel.online?.byUtm || [];
  const inStorePos = channel.inStore?.byPos || [];
  const topNewCustomers = customers.new?.top || [];
  const topRepeatCustomers = customers.repeat?.top || [];

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={2}>
        <Typography variant="h1" gutterBottom>
          End of Day Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {date ? moment(date).format("dddd, MMM D, YYYY") : "Select a date"} · Eastern time
        </Typography>
        <Box my={3}>
          <form onSubmit={runDateSearch}>
            <Box sx={{ display: "flex", gap: "10px" }}>
              <BDatePicker
                value={date}
                variant="filled"
                label="Date"
                onChange={onDateChange}
                required
                autoOk
                maxDate={maxReportDate}
                hideIcon={true}
              />
              <Button variant="contained" color="primary" type="submit" style={{ height: "56px" }}>
                <SearchIcon />
              </Button>
            </Box>
          </form>
        </Box>
      </Box>
      {showStoreFilter && (
        <Box my={2}>
          <ItemFilter
            config={[{ name: "store", options: storeStreets }]}
            onChange={onStoreFilterChange}
          />
        </Box>
      )}

      {loading ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      ) : null}

      {!loading && !error && report && (
        <>
          <Section
            title="Sales"
            description="Orders placed on this date, excluding canceled orders. Future or deferred orders may not be paid in full yet."
          >
            <Subsection>
              <Box
                sx={{
                  mb: 2,
                  p: { xs: 2.5, sm: 3 },
                  borderRadius: 1,
                  bgcolor: NESTED_BG,
                  borderLeft: `4px solid ${ACCENT}`,
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  Total Sales
                </Typography>
                <Typography variant="h2" sx={{ mt: 0.5, color: ACCENT }}>
                  {money(summary.totalSales)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {orderLabel(summary.orderCount)}
                </Typography>
              </Box>
              {/*<ShareBar segments={summarySegments} total={summaryPartsTotal} />
              <CategoryGrid columns={4}>
                {summarySegments.map((segment) => (
                  <CategoryCard
                    key={segment.key}
                    label={segment.label}
                    amount={segment.value}
                    count={summary.orderCount}
                    share={pct(segment.value, summaryPartsTotal)}
                  />
                ))}
              </CategoryGrid>*/}
            </Subsection>

            <Subsection title="Orders by Fulfillment">
              <ShareBar segments={fulfillmentSegments} total={totalSales} />
              <CategoryGrid columns={2}>
                <CategoryCard
                  label="To-Go"
                  amount={fulfillment.togo?.totalAmount}
                  count={fulfillment.togo?.orderCount}
                  share={pct(fulfillment.togo?.totalAmount, totalSales)}
                  detail={`Tips ${money(fulfillment.togo?.tips)}`}
                />
                <CategoryCard
                  label="Future"
                  amount={fulfillment.future?.totalAmount}
                  count={fulfillment.future?.orderCount}
                  share={pct(fulfillment.future?.totalAmount, totalSales)}
                  detail={`Tips ${money(fulfillment.future?.tips)}`}
                />
              </CategoryGrid>
            </Subsection>

            <Subsection title="Channel">
              <ShareBar segments={channelSegments} total={totalSales} />
              <CategoryGrid columns={1}>
                <CategoryCard
                  label="Online"
                  amount={channel.online?.totalAmount}
                  count={channel.online?.orderCount}
                  share={pct(channel.online?.totalAmount, totalSales)}
                >
                  {onlineUtms.length > 0 ? (
                    onlineUtms.map((item) => (
                      <BreakdownRow
                        key={item.key}
                        label={item.label || "direct"}
                        amount={item.totalAmount}
                        count={item.orderCount}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.75 }}>
                      No online orders
                    </Typography>
                  )}
                </CategoryCard>

                <CategoryCard
                  label="In-Store"
                  amount={channel.inStore?.totalAmount}
                  count={channel.inStore?.orderCount}
                  share={pct(channel.inStore?.totalAmount, totalSales)}
                >
                  {inStorePos.length > 0 ? (
                    inStorePos.map((pos) => {
                      const methods = getPosPaymentMethods(pos);
                      const singleMethod = methods.length === 1 ? methods[0] : null;
                      const showMethodRows = methods.length > 1;

                      return (
                        <Box key={pos.key}>
                          <BreakdownRow
                            label={pos.label}
                            amount={pos.totalAmount}
                            count={pos.orderCount}
                            detail={singleMethod ? singleMethod.label : null}
                          />
                          {showMethodRows
                            ? methods.map((method) => (
                                <BreakdownRow
                                  key={`${pos.key}-${method.key}`}
                                  label={method.label}
                                  amount={method.totalAmount}
                                  count={method.orderCount}
                                  nested
                                />
                              ))
                            : null}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.75 }}>
                      No in-store orders
                    </Typography>
                  )}
                </CategoryCard>

                <CategoryCard
                  label="Commercial"
                  amount={channel.commercial?.totalAmount}
                  count={channel.commercial?.orderCount}
                  share={pct(channel.commercial?.totalAmount, totalSales)}
                />
              </CategoryGrid>
            </Subsection>

            <Subsection title="Customers">
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {`${
                  (customers.new?.customerCount || 0) + (customers.repeat?.customerCount || 0)
                } customers`}
              </Typography>
              <ShareBar segments={customerSegments} total={customersTotal} />
              <CategoryGrid columns={2}>
                <CategoryCard
                  label="New"
                  amount={customers.new?.totalAmount}
                  count={customers.new?.customerCount}
                  share={pct(customers.new?.totalAmount, customersTotal)}
                  countNoun="customer"
                  nestedTitle="Top 5"
                >
                  {topNewCustomers.length > 0 ? (
                    topNewCustomers.map((customer) => (
                      <BreakdownRow
                        key={customer.key}
                        label={customer.name}
                        amount={customer.totalAmount}
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.75 }}>
                      No new customers
                    </Typography>
                  )}
                </CategoryCard>

                <CategoryCard
                  label="Repeat"
                  amount={customers.repeat?.totalAmount}
                  count={customers.repeat?.customerCount}
                  share={pct(customers.repeat?.totalAmount, customersTotal)}
                  countNoun="customer"
                  nestedTitle="Top 5"
                >
                  {topRepeatCustomers.length > 0 ? (
                    topRepeatCustomers.map((customer) => (
                      <BreakdownRow
                        key={customer.key}
                        label={customer.name}
                        amount={customer.totalAmount}
                        detail={
                          customer.lifetimeSpend != null
                            ? `Lifetime ${money(customer.lifetimeSpend)} · ${
                                customer.lifetimeOrderCount || 0
                              } orders`
                            : null
                        }
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1.75 }}>
                      No repeat customers
                    </Typography>
                  )}
                </CategoryCard>
              </CategoryGrid>
            </Subsection>

            {summary.orderCount === 0 && (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No orders placed on this date.
              </Typography>
            )}
          </Section>

          <Section
            title="Transactions"
            description="Payment ledger transactions collected on this date, including balance, add-on & installment payments on orders placed earlier."
          >
            <Box
              sx={{
                mb: 3,
                p: { xs: 2.5, sm: 3 },
                borderRadius: 1,
                bgcolor: NESTED_BG,
                borderLeft: `4px solid ${ACCENT}`,
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Total Payments
              </Typography>
              <Typography variant="h2" sx={{ mt: 0.5, color: ACCENT }}>
                {money(paymentsTotal)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {`${transactions.total?.count || 0} ${(transactions.total?.count || 0) === 1 ? "transaction" : "transactions"}`}
              </Typography>
            </Box>
            <ShareBar segments={paymentSegments} total={paymentsTotal} />
            <CategoryGrid columns={3}>
              {paymentSegments.map((segment) => (
                <CategoryCard
                  key={segment.key}
                  label={segment.label}
                  amount={segment.value}
                  count={transactions[segment.key]?.count || 0}
                  share={pct(segment.value, paymentsTotal)}
                  countNoun="txn"
                />
              ))}
            </CategoryGrid>
          </Section>
        </>
      )}
    </Container>
  );
}
